<script setup lang="ts">
import { ref, computed, watch, inject } from "vue";
import { getDateValues, isLeapYear } from "@/utils/dates";
import Select from "./Select.vue";
import useVuelidate, { ValidationArgs } from "@vuelidate/core";
import { nameInjectionKey } from "../keys";
import { sub, startOfDay, getDaysInMonth } from "date-fns";

defineOptions({
    inheritAttrs: false,
});

interface IOption {
    value: string | number;
    label: string;
}

interface IProps {
    modelValue: string | null;
    minDate: Date | null;
    maxDate: Date | null;
    showDayField?: boolean;
    validation?: ValidationArgs;
}

const props = withDefaults(defineProps<IProps>(), {
    showDayField: true,
    validation: undefined,
    validator: undefined,
});

const emit = defineEmits<{
    "update:modelValue": [value: string];
}>();

const name = inject(nameInjectionKey);
if (typeof name !== "string") {
    throw new Error();
}

const internalModelValue = computed(() => props.modelValue);

const v$ = useVuelidate({
    field: props.validation ?? {},
}, {
    field: internalModelValue,
});

function parseDate(dateString: string | null) {
    const [yearValue, monthValue, dayValue] = (dateString || "").split("-");
    return {
        year: yearValue ?? "",
        month: monthValue ? (Number(monthValue) - 1).toString() : "",
        day: !isNaN(Number(dayValue)) ? (Number(dayValue)).toString() : "",
    };
}

const { year, month, day } = (() => {
    const { year, month, day } = parseDate(props.modelValue);
    return {
        year: ref(year),
        month: ref(month),
        day: ref(props.showDayField ? day : "1"),
    };
})();

watch(() => props.modelValue, (newValue) => {
    if (!newValue) {
        return;
    }
    const { year: newYear, month: newMonth, day: newDay } = parseDate(newValue);
    year.value = newYear;
    month.value = newMonth;
    day.value = props.showDayField ? newDay : "1";
});

const minDate = computed(() => {
    return props.minDate !== null && !isNaN(new Date(props.minDate).getTime())
        ? props.minDate
        : startOfDay(sub(new Date(), ({ years: 100 })));
});

const maxDate = computed(() => {
    return props.maxDate !== null && !isNaN(new Date(props.maxDate).getTime())
        ? props.maxDate
        : startOfDay(new Date());
});

const availableYears = computed((): IOption[] => {
    const years: IOption[] = [];
    for (let year = maxDate.value.getFullYear(); year >= minDate.value.getFullYear(); year--) {
        years.push({
            value: year,
            label: year.toString(),
        });
    }

    return years;
});

const availableMonths = computed((): IOption[] => {
    const { year: maxYear, month: maxMonth } = getDateValues(maxDate.value);
    const { year: minYear, month: minMonth } = getDateValues(minDate.value);

    let months = [
        { value: 0, label: "January" },
        { value: 1, label: "February" },
        { value: 2, label: "March" },
        { value: 3, label: "April" },
        { value: 4, label: "May" },
        { value: 5, label: "June" },
        { value: 6, label: "July" },
        { value: 7, label: "August" },
        { value: 8, label: "September" },
        { value: 9, label: "October" },
        { value: 10, label: "November" },
        { value: 11, label: "December" },
    ];

    if (parseInt(year.value) === maxYear) {
        months = months.filter((month: IOption) => month.value as number <= maxMonth);
    }

    if (parseInt(year.value) === minYear) {
        months = months.filter((month: IOption) => month.value as number >= minMonth);
    }

    return months;
});

const availableDays = computed(() => {
    const { year: maxYear, month: maxMonth, day: maxDay } = getDateValues(maxDate.value);
    const { year: minYear, month: minMonth, day: minDay } = getDateValues(minDate.value);
    const isMaxYearAndMonth = parseInt(year.value) === maxYear && parseInt(month.value) === maxMonth;
    const isMinYearAndMonth = parseInt(year.value) === minYear && parseInt(month.value) === minMonth;

    const totalDays = getDaysInMonth(new Date(+year.value || 0, +month.value || 0));

    let days = Array.from({ length: totalDays }, (_, i) => ({
        value: i + 1,
        label: (i + 1).toString(),
    }));

    if (isMaxYearAndMonth) {
        days = days.filter((day: IOption) => day.value as number <= maxDay);
    }

    if (isMinYearAndMonth) {
        days = days.filter((day: IOption) => day.value as number >= minDay);
    }

    return days;
});

watch(day, updateFullDate);
watch(month, updateMonth);
watch(year, updateYear);

function updateMonth() {
    const validDays = availableDays.value.map((day: IOption) => day.value as number);

    if (day.value !== "" && !validDays.includes(parseInt(day.value))) {
        if (props.showDayField) {
            day.value = "";
        } else {
            day.value = availableDays.value[0].value.toString();
        }
    }

    updateFullDate();
}

function updateYear() {
    const validMonths = availableMonths.value.map((month: IOption) => month.value as number);

    if (month.value !== "" && !validMonths.includes(parseInt(month.value))) {
        month.value = "";
    } else {
        updateMonth();
    }

    handleNoLongerLeapYear();

    updateFullDate();
}

function handleNoLongerLeapYear() {
    if (!isLeapYear(parseInt(year.value)) && month.value === "1" && day.value === "29") {
        updateMonth();
    }
}

function updateFullDate() {
    if (year.value && month.value && day.value) {
        emit("update:modelValue", `${year.value}-${(Number(month.value) + 1).toString().padStart(2, "0")}-${day.value.padStart(2, "0")}`);
        v$.value.$touch();
    } else {
        emit("update:modelValue", "");
    }
}
</script>

<template>
    <div class="negative-field-group">
        <div
            class="row"
        >
            <div
                v-if="showDayField"
                class="col-md-3"
            >
                <label
                    :for="`${name}-day`"
                    class="visually-hidden-focusable"
                >
                    Day
                </label>
                <Select
                    :id="`${name}-day`"
                    v-model="day"
                    :options="availableDays"
                    :error="v$.$error"
                    label="Day"
                    placeholder="Day"
                    col="col-auto"
                    :class="[
                        'sessioncamhidetext',
                        { errorfield: v$.$error }
                    ]"
                />
            </div>
            <div class="col-md-5">
                <label
                    :for="`${name}-month`"
                    class="visually-hidden-focusable"
                >
                    Month
                </label>
                <Select
                    :id="`${name}-month`"
                    v-model="month"
                    :options="availableMonths"
                    placeholder="Month"
                    label="Month"
                    col="col-auto"
                    :class="[
                        'sessioncamhidetext',
                        { errorfield: v$.$error }
                    ]"
                />
            </div>
            <div class="col-md-4">
                <label
                    :for="`${name}-year`"
                    class="visually-hidden-focusable"
                >
                    Year
                </label>
                <Select
                    :id="`${name}-year`"
                    v-model="year"
                    :options="availableYears"
                    placeholder="Year"
                    label="Year"
                    col="col-auto"
                    :class="[
                        'sessioncamhidetext',
                        { errorfield: v$.$error }
                    ]"
                />
            </div>
        </div>
        <div
            v-if="v$?.$error"
            class="field-validation-error"
        >
            <div class="alert alert-danger">
                {{ v$.$errors[0].$message }}
            </div>
        </div>
    </div>
</template>
